import {
  useCreateSetupIntent,
  useGetSubscription,
  useListBillingPlans,
} from "@/api/stripe";
import { useOrgUsers } from "@/api/users";
import { ErrorDisplay } from "@/components/ErrorBoundary/ErrorBoundary";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PLANS } from "@/constants/plans";
import { ROUTES } from "@/constants/routes";
import LogoIcon from "@/svg/brand/cohesive-logo-icon.svg?react";
import { cn } from "@/utils";
import * as analytics from "@/utils/analytics";
import { formatCurrency } from "@/utils/currency";
import { resubscribesWithoutTrial } from "@/utils/planChange";
import { route } from "@/utils/route";
import type { BillingInterval } from "@cohesive/api-client";
import { Spinner } from "@cohesive/ui-components";
import {
  AddressElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useMutation } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, ArrowLeft, Check } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

interface TransformedPlan {
  name: string;
  display_name: string;
  monthly_price?: number;
  yearly_price?: number;
  credit_price: number;
  included_credits_per_seat: number;
  available_intervals: string[];
}

interface SelectedPlanState {
  plan: TransformedPlan;
  billingCycle: BillingInterval;
  userCount: number;
}

// Full amount billed for one period (all seats) — the due-today total when
// resubscribing without a trial, and the after-trial charge otherwise.
function periodTotal(selectedPlan: SelectedPlanState): number {
  const perSeat =
    selectedPlan.billingCycle === "year"
      ? selectedPlan.plan.yearly_price || 0
      : selectedPlan.plan.monthly_price || 0;
  return perSeat * selectedPlan.userCount;
}

// Checkout form component
function CheckoutForm({
  selectedPlan,
  onBack,
  noTrial,
}: {
  selectedPlan: SelectedPlanState;
  onBack: () => void;
  noTrial: boolean;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const createSetupIntent = useCreateSetupIntent();
  const [errorMessage, setErrorMessage] = useState<string>();

  const handleSubmit = useMutation({
    mutationFn: async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      analytics.trackBillingCheckoutSubmitted({
        planName: selectedPlan.plan.name,
        billingInterval: selectedPlan.billingCycle,
        seatCount: selectedPlan.userCount,
        isPaymentMethodUpdate: false,
      });

      if (!stripe || !elements) {
        return;
      }

      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      // Deferred-mode flow: create the SetupIntent at submit time so we don't
      // mint orphans on every form mount and never pass an undefined secret.
      const setupIntent = await createSetupIntent.mutateAsync();

      const { error } = await stripe.confirmSetup({
        elements,
        clientSecret: setupIntent.data.client_secret,
        confirmParams: {
          return_url:
            window.location.origin +
            route(
              ROUTES.ONBOARDING.CONFIRM_SETUP,
              {},
              {
                plan: selectedPlan.plan.name,
                billing_cycle: selectedPlan.billingCycle,
                user_count: String(selectedPlan.userCount),
                redirect_to: ROUTES.HOME,
              },
            ),
        },
        redirect: "always",
      });

      if (error) {
        throw new Error(error.message);
      }
    },
    onError: (error: Error) => {
      setErrorMessage(error.message);
    },
  });

  return (
    <form onSubmit={handleSubmit.mutateAsync} className="space-y-6">
      {errorMessage && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <PaymentElement
          options={{
            layout: "tabs",
            fields: {
              billingDetails: {
                name: "never",
              },
            },
          }}
        />
        <AddressElement
          options={{
            mode: "billing",
          }}
        />
      </div>

      <Button
        data-testid="submit-button"
        className="w-full"
        size="lg"
        type="submit"
        disabled={!stripe}
        isLoading={handleSubmit.isPending}
      >
        {noTrial
          ? `Subscribe — ${formatCurrency(periodTotal(selectedPlan), { hideCentsForWholeNumbers: true })} due today`
          : "Start free trial — $0 due today"}
      </Button>

      <p className="text-xs text-muted-foreground text-center">
        {noTrial ? (
          <>
            You will be charged {formatCurrency(periodTotal(selectedPlan))}{" "}
            today and then per{" "}
            {selectedPlan.billingCycle === "year" ? "year" : "month"} until you
            cancel.
          </>
        ) : (
          <>
            After the trial period, you will be charged{" "}
            {formatCurrency(periodTotal(selectedPlan))} per{" "}
            {selectedPlan.billingCycle === "year" ? "year" : "month"} unless you
            cancel before the trial expires.
          </>
        )}
      </p>

      {/* Back button for mobile */}
      <div className="lg:hidden pt-4">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          className="w-full"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Choose a different plan
        </Button>
      </div>
    </form>
  );
}

// Plan summary card for the checkout view
function PlanSummaryCard({
  selectedPlan,
  onBack,
  noTrial,
}: {
  selectedPlan: SelectedPlanState;
  onBack: () => void;
  noTrial: boolean;
}) {
  const { plan, billingCycle } = selectedPlan;
  const isPro = plan.name === PLANS.PRO;
  const monthlyEquivalent =
    billingCycle === "year"
      ? (plan.yearly_price || 0) / 12
      : plan.monthly_price || 0;

  const features = isPro
    ? [
        "All core features",
        "Standard email support (24-48h response)",
        "Organizational Administration & Billing",
        "Advanced automations and workflows",
        "Team collaboration tools",
        "Enhanced integrations",
        "Usage analytics and reporting",
        `${plan.included_credits_per_seat || 2000} credits included per seat`,
      ]
    : [
        "All core features",
        "Standard email support (24-48h response)",
        "Basic automations and workflows",
        "Individual workspace management",
        "Standard integrations",
        "Community support access",
        `${plan.included_credits_per_seat || 1500} credits included per seat`,
      ];

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        onClick={onBack}
        className="mb-4 -ml-2 text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Choose a different plan
      </Button>

      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          {noTrial
            ? "Resume your subscription."
            : "Get Cohesive free for 14 days."}
        </h1>
        <p className="text-lg text-muted-foreground mt-2">
          {noTrial
            ? `${formatCurrency(periodTotal(selectedPlan), { hideCentsForWholeNumbers: true })} due today. Cancel any time.`
            : "$0 due today. Cancel any time."}
        </p>
      </div>

      <Card
        className={`border-2 ${isPro ? "border-rust dark:border-teal/40" : "border-primary"}`}
      >
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {isPro && <div className="w-3 h-3 rounded-full bg-yellow-400" />}
              <span className="font-semibold text-lg">{plan.display_name}</span>
            </div>
            <Badge variant="secondary">
              {formatCurrency(monthlyEquivalent, {
                hideCentsForWholeNumbers: true,
              })}
              /mo
            </Badge>
          </div>

          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center gap-3 text-sm">
                <Check
                  className={`w-4 h-4 flex-shrink-0 ${isPro ? "text-teal" : "text-primary"}`}
                />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <div className="text-center">
        <p className="text-xs text-muted-foreground">
          Secure payment powered by Stripe
        </p>
      </div>
    </div>
  );
}

// Enterprise card component - matches marketing website styling
function EnterpriseCard({ onSelect }: { onSelect: () => void }) {
  return (
    <div className="relative h-full cursor-pointer" onClick={onSelect}>
      <div className="relative bg-background border rounded-2xl p-8 shadow-xs flex flex-col h-full">
        {/* Header Section */}
        <div className="text-center mb-8 flex-none">
          <h3 className="text-4xl text-foreground mb-4 font-bold h-10">
            Enterprise
          </h3>
          <div className="h-24 flex items-start justify-center pt-2">
            <p className="text-muted-foreground text-sm leading-relaxed">
              For enterprises requiring SSO, enterprise-grade security, and
              scalable deployment with SLAs, dedicated experts, and priority
              support.
            </p>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="text-center mb-8 flex-none">
          <div className="h-[60px] flex items-center justify-center mb-3">
            <div className="text-4xl font-bold text-foreground">
              Custom Pricing
            </div>
          </div>

          {/* Pill Section */}
          <div className="flex justify-center">
            <div className="bg-spectrum-orange-light/30 dark:bg-spectrum-orange-dark/30 text-lc-warning dark:text-lc-warning px-4 py-2 rounded-full text-sm font-medium inline-block">
              Talk to our team
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="mb-8 flex-none">
          <div className="h-32 flex flex-col justify-start pt-2 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-base text-foreground font-semibold">
                Credits
              </span>
              <span className="text-sm text-muted-foreground">Custom</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">
                Single Sign-On (SSO)
              </span>
              <span className="text-sm text-lc-active dark:text-lc-active font-medium">
                Yes
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Connections</span>
              <span className="text-sm text-muted-foreground">
                2,000+ Unlimited
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Tasks</span>
              <span className="text-sm text-muted-foreground">Unlimited</span>
            </div>
          </div>
        </div>

        {/* Support Section */}
        <div className="flex-1">
          <h4 className="text-foreground mb-2">Support & Billing</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>Highest-priority support with guaranteed SLAs</li>
            <li>Direct access to solutions engineering experts</li>
            <li>Dedicated account manager & custom onboarding</li>
            <li>Billing: Credit Card, Invoicing & ACH Billing</li>
          </ul>
        </div>

        {/* CTA Button */}
        <div className="flex-none relative mt-8">
          <Button
            className="w-full h-[62px] text-base"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
          >
            Contact Sales
          </Button>
        </div>
      </div>
    </div>
  );
}

// Plan selection card component - matches marketing website styling
function OnboardingPlanCard({
  plan,
  isPro,
  onSelect,
}: {
  plan: TransformedPlan;
  isPro: boolean;
  onSelect: () => void;
  disabled: boolean;
}) {
  const monthlyPrice =
    plan.yearly_price !== undefined
      ? plan.yearly_price / 12
      : plan.monthly_price || 0;

  const creditsPerSeat = isPro
    ? plan.included_credits_per_seat || 2000
    : plan.included_credits_per_seat || 1500;

  const description = isPro
    ? "For professional teams building an agentic workforce, easily creating, deploying, and managing agents and org-wide workflows."
    : "For individuals and small teams getting started with AI-powered workflows and automation.";

  const supportItems = isPro
    ? [
        "Priority email support with faster response times",
        "Chat support during business hours",
        "Billing: Credit Card",
      ]
    : [
        "Standard email support (24-48h response)",
        "Community support access",
        "Billing: Credit Card",
      ];

  return (
    <div className="relative h-full cursor-pointer" onClick={onSelect}>
      <div className="relative bg-background border rounded-2xl p-8 shadow-xs flex flex-col h-full">
        {/* Public Preview Badge - only for Pro */}
        {isPro && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <div className="bg-background border text-foreground px-4 py-2 rounded-full text-sm font-medium">
              Public Preview
            </div>
          </div>
        )}

        {/* Header Section */}
        <div className="text-center mb-8 flex-none">
          <h3 className="text-4xl text-foreground mb-4 font-bold h-10">
            {plan.display_name}
          </h3>
          <div className="h-24 flex items-start justify-center pt-2">
            <p className="text-muted-foreground text-sm leading-relaxed">
              {description}
            </p>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="text-center mb-8 flex-none">
          <div className="h-[60px] flex items-center justify-center mb-3">
            <div className="flex items-center justify-center gap-2">
              <span className="text-[40px] font-bold text-foreground">
                {formatCurrency(monthlyPrice, {
                  hideCentsForWholeNumbers: true,
                })}
                <span className="text-sm font-normal text-muted-foreground">
                  /user/month
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="mb-8 flex-none">
          <div className="h-32 flex flex-col justify-start pt-2 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-base text-foreground font-semibold">
                Credits
              </span>
              <span className="text-sm text-muted-foreground">
                {creditsPerSeat.toLocaleString()} /user/mo.
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">
                Single Sign-On (SSO)
              </span>
              <span className="text-sm text-lc-blocked dark:text-lc-blocked font-medium">
                No
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Connections</span>
              <span className="text-sm text-muted-foreground">
                {isPro ? "Standard + Pro" : "Standard"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Tasks</span>
              <span className="text-sm text-muted-foreground">Unlimited</span>
            </div>
          </div>
        </div>

        {/* Support Section */}
        <div className="flex-1">
          <h4 className="text-foreground mb-2">Support & Billing</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            {supportItems.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>

        {/* CTA Button */}
        <div className="flex-none relative mt-8">
          <Button
            className="w-full h-[62px] text-base bg-foreground text-background hover:bg-foreground/90"
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
          >
            Sign Up
          </Button>
        </div>
      </div>
    </div>
  );
}

export function SelectPlanPage() {
  const navigate = useNavigate();
  const { data: billingPlans, isLoading, error } = useListBillingPlans();
  const [selectedPlan, setSelectedPlan] = useState<SelectedPlanState | null>(
    null,
  );

  const { data: orgUsers } = useOrgUsers();
  const orgUsersCount = orgUsers?.length || 1;

  // A prior ended subscription means resubscribing is billed immediately with
  // no free trial (the trial is first-subscription-only), so the trial copy
  // below would be wrong.
  const { data: subscription } = useGetSubscription();
  const noTrial = resubscribesWithoutTrial(
    subscription?.data.stripe_subscription,
  );

  // Transform API data to group monthly/yearly plans
  const transformedPlans = useMemo(() => {
    if (!billingPlans?.data.billing_plans) return [];

    const planMap = new Map<string, TransformedPlan>();

    billingPlans.data.billing_plans.forEach((plan) => {
      const key = plan.name;
      if (!planMap.has(key)) {
        planMap.set(key, {
          name: plan.name,
          display_name: plan.display_name,
          credit_price: plan.credit_price,
          included_credits_per_seat: plan.included_credits_per_seat,
          available_intervals: [],
        });
      }

      const transformed = planMap.get(key)!;
      if (plan.billing_interval === "month") {
        transformed.monthly_price = plan.seat_price / 100;
        transformed.available_intervals.push("month");
      } else if (plan.billing_interval === "year") {
        transformed.yearly_price = plan.seat_price / 100;
        transformed.available_intervals.push("year");
      }
    });

    return Array.from(planMap.values());
  }, [billingPlans]);

  const handlePlanSelect = (plan: TransformedPlan) => {
    analytics.trackBillingPlanSelected({
      planName: plan.name,
      source: "onboarding_select_plan",
    });

    // Default to yearly if available, otherwise monthly
    const billingCycle: BillingInterval = plan.available_intervals.includes(
      "year",
    )
      ? "year"
      : "month";

    setSelectedPlan({
      plan,
      billingCycle,
      userCount: orgUsersCount,
    });
  };

  const handleBack = () => {
    setSelectedPlan(null);
  };

  const standardPlan = transformedPlans.find(
    (plan) => plan.name === PLANS.STANDARD,
  );
  const proPlan = transformedPlans.find((plan) => plan.name === PLANS.PRO);

  if (error) {
    return <ErrorDisplay message={error.message} />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <>
      <title>Choose Your Plan</title>
      <div className="h-full w-full">
        <AnimatePresence mode="wait">
          {!selectedPlan ? (
            // Plan Selection View
            <motion.div
              key="plan-selection"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className="flex items-center justify-center min-h-full py-10 px-4 sm:px-6 lg:px-8"
            >
              <div className="w-full max-w-6xl space-y-8">
                {/* Header */}
                <div className="text-center space-y-6">
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{
                      delay: 0.1,
                      type: "spring",
                      stiffness: 100,
                      damping: 15,
                    }}
                    className="flex justify-center"
                  >
                    <LogoIcon width="64" height="64" />
                  </motion.div>
                  <motion.div
                    className="space-y-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{
                      delay: 0.2,
                      duration: 0.4,
                      ease: "easeOut",
                    }}
                  >
                    <h1 className="text-4xl font-bold text-foreground tracking-tight">
                      Choose Your Plan
                    </h1>
                    <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                      {noTrial
                        ? "Choose a plan to resume your subscription."
                        : "Start your 14-day free trial today. No charge until the trial ends."}
                    </p>
                  </motion.div>
                </div>

                {/* Plan Cards */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: 0.3,
                    duration: 0.4,
                    ease: "easeOut",
                  }}
                  className={cn("grid gap-6 max-w-4xl mx-auto", {
                    "lg:grid-cols-3": transformedPlans.length === 2,
                    "lg:grid-cols-2": transformedPlans.length === 1,
                  })}
                >
                  {standardPlan && (
                    <div className="relative">
                      <OnboardingPlanCard
                        plan={standardPlan}
                        isPro={false}
                        onSelect={() => handlePlanSelect(standardPlan)}
                        disabled={false}
                      />
                    </div>
                  )}
                  {proPlan && (
                    <div className="relative">
                      <OnboardingPlanCard
                        plan={proPlan}
                        isPro={true}
                        onSelect={() => handlePlanSelect(proPlan)}
                        disabled={false}
                      />
                    </div>
                  )}
                  <div className="relative">
                    <EnterpriseCard
                      onSelect={() => {
                        analytics.trackBillingEnterpriseInquiryClicked({
                          source: "onboarding_select_plan",
                        });
                        navigate(ROUTES.ONBOARDING.ENTERPRISE_INQUIRY);
                      }}
                    />
                  </div>
                </motion.div>
              </div>
            </motion.div>
          ) : (
            // Checkout View - Responsive layout with single form
            <motion.div
              key="checkout"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="min-h-full lg:grid lg:grid-cols-2"
            >
              {/* Left side - Plan Summary (desktop only) */}
              <div className="hidden lg:flex bg-muted p-12 items-center justify-end lg:pr-20">
                <div className="max-w-md w-full">
                  <PlanSummaryCard
                    selectedPlan={selectedPlan}
                    onBack={handleBack}
                    noTrial={noTrial}
                  />
                </div>
              </div>

              {/* Right side / Mobile full - Checkout Form */}
              <div className="p-4 sm:p-8 lg:p-12 flex items-center justify-start bg-background min-h-full lg:pl-20">
                <div className="max-w-md w-full">
                  {/* Mobile header */}
                  <div className="lg:hidden mb-6">
                    <Button
                      variant="ghost"
                      onClick={handleBack}
                      className="-ml-2 mb-4 text-muted-foreground hover:text-foreground"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    <h1 className="text-2xl font-bold tracking-tight">
                      {noTrial
                        ? "Resume your subscription."
                        : "Get Cohesive free for 14 days."}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                      {noTrial
                        ? `${formatCurrency(periodTotal(selectedPlan), { hideCentsForWholeNumbers: true })} due today. Cancel any time.`
                        : "$0 due today. Cancel any time."}
                    </p>

                    {/* Mobile plan summary */}
                    <div className="mt-4 p-4 rounded-lg border bg-muted/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {selectedPlan.plan.name === PLANS.PRO && (
                            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                          )}
                          <span className="font-medium">
                            {selectedPlan.plan.display_name}
                          </span>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {formatCurrency(
                            selectedPlan.billingCycle === "year"
                              ? (selectedPlan.plan.yearly_price || 0) / 12
                              : selectedPlan.plan.monthly_price || 0,
                            { hideCentsForWholeNumbers: true },
                          )}
                          /mo
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Desktop header */}
                  <div className="hidden lg:block mb-8">
                    <h2 className="text-xl font-semibold mb-1">
                      Payment Information
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {noTrial
                        ? "Your card will be charged today to start your subscription"
                        : "Your card will not be charged until the trial ends"}
                    </p>
                  </div>

                  <CheckoutForm
                    selectedPlan={selectedPlan}
                    onBack={handleBack}
                    noTrial={noTrial}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
